/**
 * V171 — Centralized Smart Contact Configuration (SOURCE OF TRUTH)
 * ────────────────────────────────────────────────────────────────
 * Every call/WhatsApp number used across the public Anand Tutorials site
 * derives from this file. If a number needs to change in the future, change
 * it here and every entry point updates automatically.
 *
 * Do NOT duplicate these numbers elsewhere. The <SmartContactRouter />
 * component intercepts tel: and wa.me: clicks against this list and routes
 * visitors to the right team.
 */

export const CONTACTS = {
    JABALPUR: {
        key: "jabalpur",
        raw: "+919993305574",
        display: "+91 9993305574",
        label: "Jabalpur Home Tuition",
        teamLabel: "Jabalpur Team",
        service: "home_tuition",
        city: "Jabalpur",
    },
    BHOPAL: {
        key: "bhopal",
        raw: "+917974221232",
        display: "+91 7974221232",
        label: "Bhopal Home Tuition",
        teamLabel: "Bhopal Team",
        service: "home_tuition",
        city: "Bhopal",
    },
    NAGPUR: {
        key: "nagpur",
        raw: "+918087198495",
        display: "+91 8087198495",
        label: "Nagpur Home Tuition",
        teamLabel: "Nagpur Team",
        service: "home_tuition",
        city: "Nagpur",
    },
    ONLINE_1_1: {
        key: "online_1_1",
        raw: "+917000530316",
        display: "+91 7000530316",
        label: "1-on-1 Online Classes",
        teamLabel: "Online Classes Team",
        service: "online_1_1",
        city: null, // Pan India / Worldwide
    },
    MAKEOVER: {
        key: "makeover",
        raw: "+918085796268",
        display: "+91 8085796268",
        label: "1-on-1 Makeover Classes",
        teamLabel: "Makeover Team",
        service: "makeover",
        city: null,
    },
    TUTOR_WHATSAPP: {
        key: "tutor_whatsapp",
        raw: "+917610702222",
        display: "+91 7610702222",
        label: "Tutor — WhatsApp only",
        teamLabel: "Tutor Team",
        service: "tutor",
        city: null,
        // Hard constraint: this line NEVER accepts calls, only WhatsApp.
        whatsappOnly: true,
    },
};

// Order used by the parent-side alternative-routing panel.
export const PARENT_ALTERNATIVES_BY_SERVICE = {
    home_tuition: ["ONLINE_1_1"],         // "Need Online 1:1 instead?"
    online_1_1: ["JABALPUR", "BHOPAL", "NAGPUR"],  // "Need city home tuition?"
    makeover: ["ONLINE_1_1", "JABALPUR", "BHOPAL", "NAGPUR"],
};

// Strip anything that isn't digits so we can safely compare any format
// (`+91 76107 02222`, `tel:+917610702222`, `https://wa.me/917610702222`, etc.)
function _digits(s) {
    return String(s || "").replace(/\D/g, "");
}

// Map every known number (bare 10-digit and 91-prefixed) → config entry.
const _byDigits = (() => {
    const m = new Map();
    Object.values(CONTACTS).forEach((c) => {
        const d = _digits(c.raw);
        m.set(d, c);                 // "917000530316"
        if (d.startsWith("91") && d.length === 12) m.set(d.slice(2), c);  // "7000530316"
    });
    return m;
})();

/**
 * Resolve a config entry from any raw href string. Supports:
 *   tel:+917000530316        → ONLINE_1_1
 *   tel:7000530316           → ONLINE_1_1
 *   https://wa.me/917000530316?text=hi → ONLINE_1_1
 *   https://api.whatsapp.com/send?phone=917000530316 → ONLINE_1_1
 *
 * Returns `null` if the number isn't one we route.
 */
export function resolveContactFromHref(href) {
    if (!href || typeof href !== "string") return null;
    // Pull the "phone" query param from wa.me / api.whatsapp.com URLs.
    let digits = "";
    try {
        if (/^tel:/i.test(href)) {
            digits = _digits(href.slice(4));
        } else if (/wa\.me/i.test(href)) {
            // https://wa.me/<digits>?text=...
            const match = href.match(/wa\.me\/(\+?\d+)/i);
            if (match) digits = _digits(match[1]);
        } else if (/api\.whatsapp\.com\/send/i.test(href)) {
            const u = new URL(href, "https://x");
            const p = u.searchParams.get("phone");
            if (p) digits = _digits(p);
        }
    } catch (_e) { /* ignore malformed URLs */ }
    if (!digits) return null;
    // Match on 12-digit (91-prefixed) or 10-digit bare.
    return _byDigits.get(digits) || _byDigits.get(digits.slice(-10)) || null;
}

export function telHref(contact) {
    return `tel:${contact.raw}`;
}

export function waHref(contact, text) {
    const digits = _digits(contact.raw);
    const encoded = text ? `?text=${encodeURIComponent(text)}` : "";
    return `https://wa.me/${digits}${encoded}`;
}

/**
 * V178 — Universal parent/student greeting (do NOT auto-personalize by city).
 * ───────────────────────────────────────────────────────────────────────────
 * A visitor can land on the Bhopal page from a USA Google search; auto-saying
 * "I'm looking for Home Tuition in Bhopal" is misleading. The primary WhatsApp
 * CTA now uses a universal, city-free greeting. The message is personalised
 * ONLY when the visitor EXPLICITLY picks a city/service from the alternatives
 * row inside the modal (that click is a real choice, not an inference from
 * referrer URL).
 *
 *   greetingFor(contact,  "parent")                       → universal parent
 *   greetingFor(contact,  "parent", { explicit: true })   → city/service-specific
 *   greetingFor(CONTACTS.ONLINE_1_1, "parent")            → universal (same as above)
 *   greetingFor(CONTACTS.TUTOR_WHATSAPP, "tutor")         → tutor greeting
 *   greetingFor(any, "tutor")                             → tutor greeting
 *
 * `userType` falls back to "parent" (overwhelming majority); if the contact
 * is explicitly `tutor_whatsapp` the function always returns the Tutor
 * greeting regardless of userType, so the tutor line is never reachable with
 * a parent-flavoured message.
 */
export function greetingFor(contact, userType = "parent", opts = {}) {
    const explicit = !!(opts && opts.explicit);
    if (!contact) {
        return "Hi Anand Tutorials, I'm a parent/student looking for tuition support. Please share the available options, tutors, fees and demo-class details. Thank you.";
    }

    // Tutor line → ALWAYS a tutor greeting
    if (contact.key === "tutor_whatsapp" || userType === "tutor") {
        return "Hi Anand Tutorials, I'm a tutor and would like to know more about teaching opportunities and the tutor registration process. Please guide me on the next steps. Thank you.";
    }

    // EXPLICIT user selection (visitor tapped a specific city/service in the
    // alternatives row inside the modal). Only then do we personalise.
    if (explicit) {
        if (contact.service === "home_tuition" && contact.city) {
            return `Hi Anand Tutorials ${contact.city}, I'm a parent/student looking for Home Tuition in ${contact.city}. Please share available tutors, subjects, fees and demo-class details. Thank you.`;
        }
        if (contact.service === "online_1_1") {
            return "Hi Anand Tutorials, I'm a parent/student looking for 1-on-1 Online Classes. Please share available tutors, subjects, fees and demo-class details. Thank you.";
        }
        if (contact.service === "makeover") {
            return "Hi Anand Tutorials, I'm a parent interested in your 1-on-1 Makeover Classes. Please share batches, fees and the free demo details. Thank you.";
        }
    }

    // Default — universal parent/student greeting (works for every page, every
    // city, every country, every service). The receiving team can ask the
    // visitor directly which city/service they want.
    return "Hi Anand Tutorials, I'm a parent/student looking for tuition support. Please share the available options, tutors, fees and demo-class details. Thank you.";
}
